/**
 * O que faz: Consulta contratos compactos do dashboard e adapta os KPIs para os componentes atuais.
 * Onde usa: Dashboard normal/admin, gráficos, PDF e detalhes paginados.
 * Conexões: Supabase RPC, Zod, contratos do dashboard e formatos de pagamento/tempo.
 * Tabelas: RPCs SELECT em leads, agenda, vendas, chat, clinicas e ad_aliases; RLS permanece ativo.
 * Data: 31/08/2026.
 */
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { DashboardAtendimentoData, DashboardMetrics } from '@/hooks/dashboard/types';
import type { AtendimentoDashboard, ContextoDashboard, DimensaoRankingAnuncios, FiltroContatosDashboard, PaginaContatosDashboard, PeriodoDashboard, RankingOrigensDashboard, ResumoDashboard } from '@/features/dashboard/data/dashboardContracts';
import { serializarPeriodoDashboard } from '@/features/dashboard/data/dashboardContracts';
import { rotuloFormaPagamento } from '@/constants/pagamentoAgendamento';
import { classifyResponseTime, formatResponseTime } from '@/utils/responseTimeUtils';

const contagem = z.number().int().nonnegative();
const numero = z.number().finite();
const periodoSchema = z.object({ inicio: z.string().nullable(), fim: z.string().nullable() });
const origemSchema = z.object({ contatos: contagem, convertidos: contagem, taxa: numero });
const anuncioSchema = z.object({ chave: z.string(), nome: z.string(), apelido: z.string().nullable(), leads: contagem, conversoes: contagem, taxa: numero });
const totaisRanking = { totalItens: contagem, totalLeads: contagem, totalConversoes: contagem };
const resumoSchema = z.object({
  periodo: periodoSchema, atualizadoEm: z.string(), passoDias: z.number().int().positive(),
  metricas: z.object({
    totalContatos: contagem, leadsAnuncios: contagem, leadsComAdName: contagem, leadsComLinkName: contagem,
    leadsLinksPersonalizados: contagem, consultasAgendadas: contagem, consultasRealizadas: contagem,
    contatosConvertidos: contagem, taxaConversao: numero,
    conversaoPorOrigem: z.object({ anuncios: origemSchema, links: origemSchema, outros: origemSchema }),
    faturamentoRealizado: numero, faturamentoConsultas: numero, faturamentoVendas: numero,
    faturamentoPorForma: z.array(z.object({ forma: z.string(), valor: numero })),
  }),
  serie: z.array(z.object({ inicio: z.string(), fim: z.string(), todos: contagem, anuncios: contagem,
    links_exclusivos: contagem, links_rastreados: contagem, outros: contagem, meta: contagem, google: contagem })).max(90),
  semana: z.array(z.object({ dia: z.number().int().min(0).max(6), todos: contagem, anuncios: contagem })).length(7),
  anuncios: z.object({ itens: z.array(anuncioSchema).max(10), ...totaisRanking }),
  links: z.object({ itens: z.array(z.object({ chave: z.string(), nome: z.string(), leads: contagem, conversoes: contagem })).max(10), ...totaisRanking }),
  categorias: z.array(z.object({ category: z.string(), conversions: contagem })).max(10),
});
const amostraSchema = z.object({ quantidade: contagem, media: numero.nullable() });
const atendimentoSchema = z.object({ periodo: periodoSchema, atualizadoEm: z.string(), mensagensEnviadas: contagem,
  mensagensRecebidas: contagem, totalRespostas: contagem, media: numero.nullable(), humano: amostraSchema,
  ia: amostraSchema, comercial: amostraSchema, distribuicao: z.object({ ate30min: contagem, de30mina1h: contagem, de1ha4h: contagem, acimaDe4h: contagem }) });
const paginaAnunciosSchema = z.object({ itens: z.array(anuncioSchema).max(50), ...totaisRanking, pagina: contagem, tamanho: contagem });
const rankingOrigensSchema = z.object({
  dimensao: z.enum(['anuncio', 'conjunto', 'campanha']), metaConectado: z.boolean(),
  itens: z.array(anuncioSchema).max(500), ...totaisRanking, semOrigem: contagem,
});
const cursorSchema =z.object({ created_at: z.string(), id: z.string().uuid() });
const paginaContatosSchema = z.object({
  itens: z.array(z.object({ id: z.string().uuid(), clinica_id: z.string().uuid(), nome: z.string().nullable(),
    telefone: z.string().nullable(), created_at: z.string(), ad_name: z.string().nullable(), anuncio: z.string().nullable(),
    link_name: z.string().nullable(), origem_lead: z.string().nullable() })).max(100), proximoCursor: cursorSchema.nullable(),
});

// O RPC não devolve o rótulo amigável de forma de pagamento; o serviço o adiciona
// depois da validação. Manter o tipo intermediário explícito evita que o genérico
// do Zod transforme campos obrigatórios em opcionais no TypeScript.
type ResumoRpc = Omit<ResumoDashboard, 'metricas'> & {
  metricas: Omit<ResumoDashboard['metricas'], 'faturamentoPorForma'> & {
    faturamentoPorForma: Array<{ forma: string; valor: number }>;
  };
};

type NomeRpcDashboard = 'get_dashboard_resumo_v1' | 'get_dashboard_atendimento_v1' | 'get_dashboard_anuncios_v1' | 'get_dashboard_contatos_v1'
  | 'get_dashboard_ranking_origens_v1' | 'get_dashboard_contatos_origem_meta_v1';

async function consultarRpcDashboard<TSchema extends z.ZodTypeAny>(
  nome: NomeRpcDashboard,
  parametros: Record<string, unknown>,
  schema: TSchema,
  signal?: AbortSignal,
): Promise<z.output<TSchema>> {
  // UUID inválido não pode disparar uma leitura sem clínica. O banco ainda valida identidade e RLS.
  if (!z.string().uuid().safeParse(parametros.p_clinica_id).success) throw new Error('Clínica inválida para o dashboard.');
  signal?.throwIfAborted();
  // RPC somente leitura: clínica/datas e filtros explícitos; não registrar payloads ou dados pessoais.
  let consulta = supabase.rpc(nome, parametros);
  if (signal) consulta = consulta.abortSignal(signal);
  const { data, error } = await consulta;
  signal?.throwIfAborted();
  // Não há fallback ao download integral: uma implantação incompleta precisa aparecer como erro recuperável.
  if (error) throw new Error(error.code === '42501' ? 'Você não tem acesso aos dados desta clínica.' : 'Não foi possível carregar o dashboard. Tente novamente.');
  const resultado = schema.safeParse(data);
  if (!resultado.success) throw new Error('Os dados recebidos do dashboard estão incompletos. Atualize e tente novamente.');
  return resultado.data;
}

const parametrosPeriodo = (clinicaId: string, periodo: PeriodoDashboard) => ({ p_clinica_id: clinicaId, p_inicio: periodo.inicio, p_fim: periodo.fim });

export async function buscarResumoDashboard(clinicaId: string, periodo: PeriodoDashboard, signal?: AbortSignal): Promise<ResumoDashboard> {
  const dados = await consultarRpcDashboard('get_dashboard_resumo_v1', parametrosPeriodo(clinicaId, periodo), resumoSchema, signal) as unknown as ResumoRpc;
  return { ...dados, metricas: { ...dados.metricas, faturamentoPorForma: dados.metricas.faturamentoPorForma.map(item => ({
    ...item, rotulo: item.forma === 'venda_crm' ? 'Vendas do CRM' : rotuloFormaPagamento(item.forma),
  })) } };
}

export const buscarAtendimentoDashboard = (clinicaId: string, periodo: PeriodoDashboard, signal?: AbortSignal): Promise<AtendimentoDashboard> =>
  consultarRpcDashboard('get_dashboard_atendimento_v1', parametrosPeriodo(clinicaId, periodo), atendimentoSchema, signal) as Promise<AtendimentoDashboard>;

export interface FiltrosAnunciosDashboard {
  busca: string; ordem: 'leads' | 'conversoes' | 'taxa' | 'nome'; direcao: 'asc' | 'desc'; pagina: number; tamanho: 20 | 50;
}
export const buscarAnunciosDashboard = (contexto: ContextoDashboard, filtros: FiltrosAnunciosDashboard, signal?: AbortSignal) =>
  consultarRpcDashboard('get_dashboard_anuncios_v1', { ...parametrosPeriodo(contexto.clinicaId, contexto.periodo),
    p_busca: filtros.busca, p_ordem: filtros.ordem, p_direcao: filtros.direcao, p_pagina: filtros.pagina, p_tamanho: filtros.tamanho,
  }, paginaAnunciosSchema, signal) as Promise<import('@/features/dashboard/data/dashboardContracts').PaginaAnunciosDashboard>;

// RPC get_dashboard_ranking_origens_v1 (somente leitura): leads/conversões do período agrupados por
// anúncio (nome gravado no lead) ou por conjunto/campanha (último toque confirmado pela Meta).
// Filtros: clínica do contexto, período e agrupamento de lista fechada. Até 500 linhas por resposta.
export const buscarRankingOrigensDashboard = (contexto: ContextoDashboard, dimensao: DimensaoRankingAnuncios, signal?: AbortSignal) =>
  consultarRpcDashboard('get_dashboard_ranking_origens_v1', { ...parametrosPeriodo(contexto.clinicaId, contexto.periodo),
    p_dimensao: dimensao,
  }, rankingOrigensSchema, signal) as Promise<RankingOrigensDashboard>;

export const buscarContatosDashboard = (contexto: ContextoDashboard, filtro: FiltroContatosDashboard,
  cursor: PaginaContatosDashboard['proximoCursor'], tamanho: 20 | 50 | 100, signal?: AbortSignal) => {
  const paginacao = { p_cursor_data: cursor?.created_at ?? null, p_cursor_id: cursor?.id ?? null, p_tamanho: tamanho };
  // Conjunto/campanha vêm da Meta (lead_ad_attributions), lidos por uma RPC própria e somente leitura:
  // get_dashboard_contatos_origem_meta_v1 filtra clínica, período e o último toque confirmado do lead.
  // A resposta tem o mesmo formato, então o modal de leads funciona igual para anúncio, conjunto e campanha.
  if (filtro.tipo === 'conjunto' || filtro.tipo === 'campanha') {
    return consultarRpcDashboard('get_dashboard_contatos_origem_meta_v1', { ...parametrosPeriodo(contexto.clinicaId, contexto.periodo),
      p_dimensao: filtro.tipo, p_chave: filtro.chave, ...paginacao,
    }, paginaContatosSchema, signal) as Promise<PaginaContatosDashboard>;
  }
  return consultarRpcDashboard('get_dashboard_contatos_v1', { ...parametrosPeriodo(contexto.clinicaId, contexto.periodo),
    p_tipo: filtro.tipo, p_chave: 'chave' in filtro ? filtro.chave : null, ...paginacao,
  }, paginaContatosSchema, signal) as Promise<PaginaContatosDashboard>;
};

export function adaptarAtendimentoDashboard(dados: AtendimentoDashboard): DashboardAtendimentoData {
  // Sem amostra, exibir indisponibilidade em vez de uma resposta artificial de zero minutos.
  const tempoMedioResposta = dados.totalRespostas > 0 && dados.media !== null ? {
    tempoMedioMinutos: dados.media, tempoMedioFormatado: formatResponseTime(dados.media), classificacao: classifyResponseTime(dados.media),
    detalhes: {
      tempoMedioHumano: dados.humano.media ?? 0, tempoMedioHumanoFormatado: dados.humano.media === null ? 'Sem respostas' : formatResponseTime(dados.humano.media),
      tempoMedioIA: dados.ia.media ?? 0, tempoMedioIAFormatado: dados.ia.media === null ? 'Sem respostas' : formatResponseTime(dados.ia.media),
      tempoMedioComercial: dados.comercial.media ?? 0, tempoMedioComercialFormatado: dados.comercial.media === null ? 'Sem respostas' : formatResponseTime(dados.comercial.media),
      distribuicao: dados.distribuicao,
    }, variacao: 0, variacaoDisponivel: false,
  } : null;
  return { mensagensEnviadas: dados.mensagensEnviadas, mensagensRecebidas: dados.mensagensRecebidas, tempoMedioResposta };
}

export function adaptarResumoDashboard(resumo: ResumoDashboard): DashboardMetrics {
  // Compatibilidade dos cards não implica recriar contatos sintéticos nem baixar registros individuais.
  return { ...resumo.metricas, resumo, rawLeads: [],
    leadsParaGrafico: resumo.serie.map(p => ({ label: p.inicio === p.fim ? format(parseISO(p.inicio),'dd/MM') : `${format(parseISO(p.inicio),'dd/MM')}–${format(parseISO(p.fim),'dd/MM')}`, leads: p.todos })),
    conversoesPorCategoria: resumo.categorias,
    leadsPorAnuncio: resumo.anuncios.itens.map(p => ({ anuncio: p.nome, leads: p.leads, conversoes: p.conversoes })),
    leadsPorLink: resumo.links.itens.map(p => ({ linkName: p.nome, leads: p.leads, conversoes: p.conversoes, source: 'Link personalizado' })),
    mensagensEnviadas: 0, mensagensRecebidas: 0, tempoMedioResposta: null,
    variacaoContatos: 0, variacaoConsultas: 0, variacaoConsultasRealizadas: 0, variacaoConversao: 0, variacaoFaturamento: 0,
    variacaoLeadsAnuncios: 0, variacaoLeadsAdName: 0, variacaoLeadsLinkName: 0, variacaoLeadsLinksPersonalizados: 0,
    variacaoMensagensEnviadas: 0, variacaoMensagensRecebidas: 0,
  };
}

// Adaptadores para consumidores antigos: mantêm assinatura, mas usam exclusivamente RPCs compactas.
export async function fetchDashboardData(clinicaId: string, inicio: Date | null, fim: Date | null, signal?: AbortSignal, options: { carregarAtendimento?: boolean } = {}) {
  const periodo = serializarPeriodoDashboard(inicio, fim);
  const [resumo, atendimento] = await Promise.all([
    buscarResumoDashboard(clinicaId, periodo, signal),
    options.carregarAtendimento === false ? Promise.resolve(null) : buscarAtendimentoDashboard(clinicaId, periodo, signal),
  ]);
  return { ...adaptarResumoDashboard(resumo), ...(atendimento ? adaptarAtendimentoDashboard(atendimento) : {}) };
}

export async function fetchDashboardAtendimentoData(clinicaId: string, inicio: Date | null, fim: Date | null, signal?: AbortSignal) {
  return adaptarAtendimentoDashboard(await buscarAtendimentoDashboard(clinicaId, serializarPeriodoDashboard(inicio, fim), signal));
}
